import {
  getIdeaSubmissionChecklist as getCoreIdeaSubmissionChecklist,
  type getSubmissionChecklist,
} from "simplydo/core/submissibility";
import type { OpenAPI } from "simplydo/interfaces";
import type { IdeaWithSubmissibilityStatus } from "simplydo/interfaces/types/ideaTypes";

import type { UserMe } from "components/ideas/types";

export const SUBMISSION_CHECKLIST_ID = "idea-submission-checklist";
export const SUBMISSION_SECTION_KEY = "submission";

/** The submission checklist is shown to editors of draft ideas, outside of merges and assessments */
export const shouldShowSubmissionSection = ({
  idea,
  canEdit,
  isAssessingSelectedRound,
}: {
  idea:
    | {
        parent?: unknown;
        children?: unknown;
        ownerChallenge?: unknown;
        isSubmitted?: boolean;
      }
    | null
    | undefined;
  canEdit: boolean;
  isAssessingSelectedRound: boolean;
}) =>
  !!idea &&
  !(idea.parent || idea.children || isAssessingSelectedRound) &&
  canEdit &&
  !!idea.ownerChallenge &&
  !idea.isSubmitted;

type ChecklistIdea = IdeaWithSubmissibilityStatus & {
  ownerIdeaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
};

type SubmissionChecklist = ReturnType<typeof getSubmissionChecklist>;

/*
  The checklist is needed by the idea body and both submit buttons. They all read the same idea
  object from the query cache, so the result is cached per idea object and recalculated whenever
  the idea is refetched or updated.
*/
const checklistCache = new WeakMap<
  ChecklistIdea,
  { user: UserMe | null | undefined; checklist: SubmissionChecklist }
>();

export const getIdeaSubmissionChecklist = (
  user: UserMe | null | undefined,
  idea: ChecklistIdea | undefined,
): SubmissionChecklist => {
  if (!idea) {
    return [];
  }
  const cached = checklistCache.get(idea);
  if (cached && cached.user === user) {
    return cached.checklist;
  }
  const checklist = getCoreIdeaSubmissionChecklist(user, idea);
  checklistCache.set(idea, { user, checklist });
  return checklist;
};

export const scrollToSubmissionChecklist = () => {
  const checklist = document.getElementById(SUBMISSION_CHECKLIST_ID);
  if (!checklist) {
    return;
  }
  checklist.scrollIntoView({ behavior: "smooth", block: "center" });
  checklist.focus({ preventScroll: true });
};
